import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  MDXEditor, type MDXEditorMethods,
  headingsPlugin, listsPlugin, quotePlugin, linkPlugin, linkDialogPlugin,
  imagePlugin, codeBlockPlugin, codeMirrorPlugin, frontmatterPlugin,
  thematicBreakPlugin, markdownShortcutPlugin, jsxPlugin, tablePlugin,
  diffSourcePlugin, toolbarPlugin,
  UndoRedo, BoldItalicUnderlineToggles, BlockTypeSelect,
  CreateLink, InsertImage, InsertTable, ListsToggle, InsertThematicBreak,
  DiffSourceToggleWrapper,
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { api, AuthError } from '@/lib/api';
import { jsxComponentDescriptors } from '@/lib/mdxeditorJsxComponents';
import { makePlaceholderName, extractReferencedFilenames } from '@/lib/pendingImages';
import { makeImageResolver, imageFolderForMdx } from '@/lib/imageResolver';

export type FileEditorMode =
  | { kind: 'existing'; prNumber: number }
  | { kind: 'new'; initialContent: string };

interface Props {
  mode: FileEditorMode;
  token: string;
  filePath: string;
  previewOrigin?: string;
  prTitle?: string;
  // Notifies parent: a save committed (existing mode only); parent appends a
  // pending review comment referencing the commit.
  onSaveCommit?: (entry: { filePath: string; shortSha: string }) => void;
  // Notifies parent: first save in new mode created a PR.
  onFirstSave?: (result: { prNumber: number; commitSha: string }) => void;
  onAuthError: () => void;
  // Overrides the default button label when mode.kind === 'new'.
  actionLabel?: { save: string; saving: string };
  // Notifies parent that the in-editor markdown changed, so the Preview tab
  // can re-render. Includes the current pending-image blob URL map for the
  // resolver.
  onContentChange?: (markdown: string, pendingBlobUrls: Map<string, string>) => void;
  // Notifies parent when the editor has unsaved changes (true) or is clean (false).
  onDirtyChange?: (dirty: boolean) => void;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; content: string; loadedSha: string }
  | { kind: 'error'; message: string };

interface PendingImage {
  file: File;
  blobUrl: string;
}

export function FileEditor(props: Props) {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [staleOpen, setStaleOpen] = useState<{ newSha: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const editorRef = useRef<MDXEditorMethods | null>(null);
  // Dirty detection uses two refs rather than content comparison against
  // state.content, because MDXEditor normalizes the markdown on mount (e.g.
  // table formatting, trailing newlines) and the normalized form may not match
  // the raw server content even after trim. Instead we:
  //   1. Collect any pre-ready onChange calls into cleanBaselineRef
  //      (MDXEditor's normalization pass, or nothing if content is already clean).
  //   2. Flip readyForDirtyRef in a useEffect — React guarantees child effects
  //      (MDXEditor's internal ones) run before parent effects, so by the time
  //      we flip the flag all normalization onChanges have already been captured.
  //   3. After the flag is set, compare onChange output to the baseline.
  const readyForDirtyRef = useRef(false);
  const cleanBaselineRef = useRef('');

  // filename → { file, blobUrl }. Stays in memory until save commits.
  const pendingRef = useRef<Map<string, PendingImage>>(new Map());
  // Cache for images fetched from the repo API (bare filenames, no previewOrigin).
  const repoBlobCacheRef = useRef<Map<string, string>>(new Map());

  const markClean = () => {
    setDirty(false);
    props.onDirtyChange?.(false);
  };

  const markDirty = () => {
    setDirty(true);
    props.onDirtyChange?.(true);
  };

  const emitContent = (markdown: string) => {
    if (!props.onContentChange) return;
    const blobUrls = new Map<string, string>();
    for (const [name, pi] of pendingRef.current) blobUrls.set(name, pi.blobUrl);
    props.onContentChange(markdown, blobUrls);
  };

  const load = async () => {
    markClean();
    setLastSaved(null);
    setState({ kind: 'loading' });
    try {
      if (props.mode.kind === 'new') {
        const content = props.mode.initialContent;
        cleanBaselineRef.current = content;
        setState({ kind: 'ready', content, loadedSha: '' });
        // React 18 may batch the loading→ready setState calls, keeping the
        // editor mounted. Call setMarkdown imperatively so it always updates.
        editorRef.current?.setMarkdown(content);
        props.onContentChange?.(content, new Map());
      } else {
        const data = await api.getFile(props.token, props.mode.prNumber, props.filePath);
        cleanBaselineRef.current = data.content;
        setState({ kind: 'ready', content: data.content, loadedSha: data.headSha });
        editorRef.current?.setMarkdown(data.content);
        props.onContentChange?.(data.content, new Map());
      }
    } catch (e) {
      if (e instanceof AuthError) { props.onAuthError(); return; }
      setState({ kind: 'error', message: e instanceof Error ? e.message : 'load failed' });
    }
  };

  const modeKey = props.mode.kind === 'existing' ? String(props.mode.prNumber) : 'new';
  useEffect(() => { void load(); }, [modeKey, props.filePath]);

  const loadedSha = state.kind === 'ready' ? state.loadedSha : null;
  useEffect(() => {
    if (loadedSha === null) { readyForDirtyRef.current = false; return; }
    readyForDirtyRef.current = true;
    return () => { readyForDirtyRef.current = false; };
  }, [loadedSha]);

  // Revoke object URLs on unmount to avoid leaks.
  useEffect(() => {
    return () => {
      for (const pi of pendingRef.current.values()) URL.revokeObjectURL(pi.blobUrl);
      pendingRef.current.clear();
      for (const u of repoBlobCacheRef.current.values()) URL.revokeObjectURL(u);
      repoBlobCacheRef.current.clear();
    };
  }, []);

  const handleImageUpload = async (file: File): Promise<string> => {
    // Generate a placeholder filename and stash the file. We do NOT contact
    // the server here — the image only commits on Save, atomically with MDX.
    const filename = makePlaceholderName(file.name || 'image.png');
    const blobUrl = URL.createObjectURL(file);
    pendingRef.current.set(filename, { file, blobUrl });
    return filename;
  };

  const handleImagePreview = async (src: string): Promise<string> => {
    if (!src) return '';
    // Pending upload (in-memory blob).
    const pending = pendingRef.current.get(src);
    if (pending) return pending.blobUrl;
    // Absolute URLs pass through.
    if (src.startsWith('http') || src.startsWith('data:') || src.startsWith('/')) return src;
    // Bare filename with a preview origin — construct preview URL.
    if (props.previewOrigin) {
      const folder = imageFolderForMdx(props.filePath);
      return `${props.previewOrigin}${folder}/${src}`;
    }
    // Bare filename, no preview origin — fetch from the repo API and cache as blob URL.
    const cached = repoBlobCacheRef.current.get(src);
    if (cached) return cached;
    const folder = imageFolderForMdx(props.filePath);
    if (!folder) return src;
    try {
      const blobUrl = await api.repoImage(props.token, `public${folder}/${src}`);
      repoBlobCacheRef.current.set(src, blobUrl);
      return blobUrl;
    } catch {
      return src;
    }
  };

  const handleSave = async () => {
    if (state.kind !== 'ready') return;
    setSaving(true);
    try {
      const markdown = editorRef.current?.getMarkdown() ?? state.content;

      if (props.mode.kind === 'new') {
        const res = await api.directPr(props.token, {
          filePath: props.filePath,
          mdxContent: markdown,
          prTitle: props.prTitle ?? props.filePath,
        });
        setState({ kind: 'ready', content: markdown, loadedSha: res.commitSha });
        cleanBaselineRef.current = markdown;
        setLastSaved(new Date());
        markClean();
        toast.success('Now creating preview for the article. Please refresh your page.');
        props.onFirstSave?.({ prNumber: res.prNumber, commitSha: res.commitSha });
      } else {
        const head = await api.getHead(props.token, props.mode.prNumber);
        if (head.headSha !== state.loadedSha) {
          setStaleOpen({ newSha: head.headSha });
          return;
        }

        // Only send images that are still referenced. Drop the rest.
        const referenced = new Set(extractReferencedFilenames(markdown));
        const images: { filename: string; file: File }[] = [];
        for (const [name, pi] of pendingRef.current) {
          if (referenced.has(name)) images.push({ filename: name, file: pi.file });
        }

        const res = await api.commitEdit(props.token, props.mode.prNumber, {
          filePath: props.filePath,
          mdxContent: markdown,
          expectedHeadSha: state.loadedSha,
          images,
        });

        // Keep blob URLs alive for the rest of the editor's lifetime so the
        // editor's imagePreviewHandler can still resolve just-committed images
        // (Vercel takes 1–2 min to rebuild and serve them via the preview
        // origin). The unmount effect revokes them all when the editor closes.

        // Advance loadedSha to our new commit so subsequent saves don't see
        // their own commit as a stale-head conflict.
        setState({ kind: 'ready', content: markdown, loadedSha: res.commitSha });
        cleanBaselineRef.current = markdown;
        setLastSaved(new Date());
        markClean();

        props.onSaveCommit?.({
          filePath: props.filePath,
          shortSha: res.commitSha.slice(0, 8),
        });
        toast.success('Saved — Claude will clean up on "Request changes".');
      }
    } catch (e) {
      if (e instanceof AuthError) { props.onAuthError(); return; }
      const msg = e instanceof Error ? e.message : 'save failed';
      toast.error('Save failed', { description: msg });
    } finally {
      setSaving(false);
    }
  };

  if (state.kind === 'loading') {
    return <div className="p-4 text-sm text-muted-foreground">Loading file…</div>;
  }
  if (state.kind === 'error') {
    return (
      <div className="p-4 text-sm text-destructive">
        Couldn't load file: {state.message}
        <Button size="sm" variant="outline" className="ml-2" onClick={load}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 overflow-auto">
        <MDXEditor
          ref={editorRef}
          markdown={state.content}
          onChange={(markdown) => {
            if (!readyForDirtyRef.current) {
              // Pre-ready: MDXEditor's normalization pass. Capture the
              // normalized output as the clean baseline so subsequent
              // comparisons use the same representation.
              cleanBaselineRef.current = markdown;
            } else {
              if (markdown !== cleanBaselineRef.current) {
                markDirty();
              } else {
                markClean();
              }
            }
            emitContent(markdown);
          }}
          plugins={[
            toolbarPlugin({
              toolbarContents: () => (
                <DiffSourceToggleWrapper>
                  <UndoRedo />
                  <BoldItalicUnderlineToggles />
                  <BlockTypeSelect />
                  <CreateLink />
                  <InsertImage />
                  <InsertTable />
                  <ListsToggle />
                  <InsertThematicBreak />
                </DiffSourceToggleWrapper>
              ),
            }),
            headingsPlugin(),
            listsPlugin(),
            quotePlugin(),
            linkPlugin(),
            linkDialogPlugin(),
            imagePlugin({
              imageUploadHandler: handleImageUpload,
              imagePreviewHandler: handleImagePreview,
            }),
            tablePlugin(),
            thematicBreakPlugin(),
            frontmatterPlugin(),
            codeBlockPlugin({ defaultCodeBlockLanguage: 'txt' }),
            codeMirrorPlugin({ codeBlockLanguages: { txt: 'Plain', mdx: 'MDX', ts: 'TypeScript' } }),
            jsxPlugin({ jsxComponentDescriptors }),
            diffSourcePlugin({ viewMode: 'rich-text', diffMarkdown: state.content }),
            markdownShortcutPlugin(),
          ]}
        />
      </div>
      <div className="border-t p-2 flex items-center justify-between gap-2">
        <div className="text-xs text-muted-foreground font-mono truncate min-w-0">
          {props.filePath}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {props.mode.kind === 'existing' && dirty && (
            <span className="text-xs text-muted-foreground">
              {lastSaved
                ? `Changes made · last saved ${lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                : 'Changes made'}
            </span>
          )}
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving || (props.mode.kind === 'existing' ? !dirty : !!(props.actionLabel && !dirty))}
          >
            {saving
              ? (props.mode.kind === 'existing' ? 'Saving…' : (props.actionLabel?.saving ?? 'Creating…'))
              : (props.mode.kind === 'existing' ? 'Save edit' : (props.actionLabel?.save ?? 'Create article'))}
          </Button>
        </div>
      </div>

      <Dialog open={!!staleOpen} onOpenChange={(o) => !o && setStaleOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>PR has new commits</DialogTitle>
            <DialogDescription>
              This branch has advanced since you opened the editor. Reload to
              edit against the latest version. Your in-progress changes will
              be discarded.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStaleOpen(null)}>
              Keep editing
            </Button>
            <Button
              onClick={() => {
                setStaleOpen(null);
                void load();
              }}
            >
              Reload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
